/**
 * Display-only helpers for the absence counting basis.
 *
 * Mirrors the backend `BasisForAbsenceType` rule: some absence types are
 * counted in work days (only days the employee was actually scheduled),
 * while others are counted in calendar days (every day in the range).
 *
 * This module is purely presentational — it does not perform any counting,
 * it only tells the UI which basis a given absence type uses and renders a
 * localized label for an already-computed count.
 */

import type { I18n } from '@lingui/core';
import { AbsenceType } from '@/types/employeeAbsences';
import type { AbsenceTypeValue } from '@/types/employeeAbsences';

export type AbsenceCountBasis = 'WORK_DAYS' | 'CALENDAR_DAYS';

/**
 * Returns the counting basis for an absence type.
 *
 * VACATION / PERSONAL / TRAINING / COMP_TIME / OTHER are counted in work days.
 * SICK / ACCIDENT / CHILD_SICK are counted in calendar days.
 *
 * @param type - The absence type.
 * @returns 'WORK_DAYS' or 'CALENDAR_DAYS'.
 */
export function absenceCountBasis(type: AbsenceTypeValue): AbsenceCountBasis {
  switch (type) {
    case AbsenceType.SICK:
    case AbsenceType.ACCIDENT:
    case AbsenceType.CHILD_SICK:
      return 'CALENDAR_DAYS';
    case AbsenceType.VACATION:
    case AbsenceType.PERSONAL:
    case AbsenceType.TRAINING:
    case AbsenceType.COMP_TIME:
    case AbsenceType.OTHER:
      return 'WORK_DAYS';
    default:
      return 'WORK_DAYS';
  }
}

/**
 * Returns a localized "{n} work days" / "{n} calendar days" label for a count,
 * choosing the wording from the absence type's counting basis.
 *
 * Uses Lingui ICU pluralization via the `values` mechanism — the count is
 * passed as an ICU parameter, never string-interpolated into the translated
 * message. The ICU source string doubles as the catalog id, so Lingui resolves
 * the correct plural form against either the compiled catalog or the source
 * fallback.
 *
 * @param count - The already-computed number of days.
 * @param type - The absence type (determines work vs. calendar days).
 * @param i18n - Lingui i18n instance for localized output.
 * @returns A localized day-count label, e.g. "4 calendar days".
 */
export function absenceCountLabel(
  count: number,
  type: AbsenceTypeValue,
  i18n: I18n
): string {
  if (absenceCountBasis(type) === 'CALENDAR_DAYS') {
    return i18n._({
      id: 'absence.count.calendarDays',
      message: '{count, plural, one {# calendar day} other {# calendar days}}',
      values: { count },
    });
  }
  return i18n._({
    id: 'absence.count.workDays',
    message: '{count, plural, one {# work day} other {# work days}}',
    values: { count },
  });
}
